import { getCookie, deleteCookie, buildRequest, logout } from "./auth.js";
import UnauthorisedError from '../errors/UnauthorisedError.js';
import { distributors, endUsers, opticians } from "../components/Helpers/userTypes.js";

export async function getUsersEmailAddress() {
    const url = `/Account/GetCoWorkers`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            } else {
                throw new Error(`Failed to fetch account: ${response.status} ${response.statusText}`);
            }
        }

        const data = await response.json();

        for (const account of data) {
            for (const coworker of account.lstCoWorkers) {
                if (coworker.fullname === "My EyeTicket History") {
                    return coworker.eaddress;
                }
            }
        }

        throw new Error("No matching fullname found");

    } catch (error) {
        throw new Error(error.message);
    }
}


export async function fetchAccount() {
    const url = `/Account/GetAccountProfile`;
    try {
        const response = await buildRequest(url);

        if (!response.ok) {

            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch account: ${response.status} ${response.statusText}`);
            }
        }

        var data = await response.json();

        if (data && data.Optician_Profile) {
            data.Optician_Profile.bank_document_base64 = null;
            data.Optician_Profile.bank_document_filename = null;
            if (typeof data.Optician_Profile.bank_document_exists !== 'boolean') {
                data.Optician_Profile.bank_document_exists = false;
            }
            if (data.Optician_Profile.bank_document_type == null) {
                data.Optician_Profile.bank_document_type = '-';
            }
        }

        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchLinkedAccounts() {
    const url = `/Account/GetAccountsLinkedByEmail`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {

            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch linked accounts: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchCoWorkers() {
    const url = `/Account/GetCoWorkers`;
    try {
        const response = await buildRequest(url);
        if (!response.ok) {

            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch linked CoWorkers: ${response.status} ${response.statusText}`);
            }
        }

        const coWorkers = await response.json();

        if (coWorkers[0]) {
            const combinedCoWorkers = coWorkers.reduce((acc, curr) => {
              const accountId = curr.Header.AccountId;
              const coWorkersWithAccountName = curr.lstCoWorkers.map(coWorker => ({
                ...coWorker,
                AccountName: accountId
              }));
              return acc.concat(coWorkersWithAccountName);
            }, []);
  
            return combinedCoWorkers;
          }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchTitles() {
    const url = `/Account/GetGenderTitles`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {

            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch titles: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function updateAccount(accountData) {
    const url = `/Account/UpdateAccountProfile`;

    try {

        const response = await buildRequest(url, 'PUT', accountData)
        const text = await response.text();

        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update account: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update account: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function resetPassword(userId, userEmail, userPassword) {
    const url = `/Account/ChangePassword`;

    const payload = {
        iUserId: userId,
        cUserEmail: userEmail,
        cNewPassword: userPassword
    };

    try {
        const response = await buildRequest(url, 'POST', payload);

        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update account: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update account: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchTheme() {
    const url = `/Account/GetCustomerWebThemeByEmail`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch account: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function createTheme(theme, accountId) {
    const url = `/Account/AddCustomerWebTheme`;
    const payload = {
        sidebar: theme.sidebar,
        menuContent: theme.menuContent,
        hover: theme.hoverColour,
        font: theme.font,
        applicationName: theme.appName,
        accountID: accountId.toString(),
        logo_image: "string",
        logo_imageBin: theme.image
    };

    try {
        const response = await buildRequest(url, 'POST', payload);

        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to create theme: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to create theme: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function updateTheme(theme, accountId) {
    const url = `/Account/UpdateCustomerWebTheme`;
    const payload = {
        sidebar: theme.sidebar,
        menuContent: theme.menuContent,
        hover: theme.hoverColour,
        font: theme.font,
        applicationName: theme.appName,
        accountID: accountId.toString(),
        logo_image: "string",
        logo_imageBin: theme.image,
        ct_unqID: theme.themeId
    };

    try {
        const response = await buildRequest(url, 'PUT', payload);
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to create theme: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to create theme: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchDashboard(account, accountType = 'CustomerOfDistributor') {
    if (endUsers.includes(accountType)) {
        accountType = "EyeTicket/GetEyeTicketDashboardSummary";
    } else if (distributors.includes(accountType)) {
        accountType = "Distributor/GetDistributorDashboardSummary";
    } else if (opticians.includes(accountType)) {
        accountType = "Optician/GetOpticianDashboardSummary";
    } else {
        accountType = "EyeTicket/GetEyeTicketDashboardSummary";
    }

    const url = `/${accountType}/${account}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch dashboard: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchHomeAlerts() {
    const url = `/Menus/GetMessageCentre`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {

            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch messages: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}